import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/shared/api/client'
import type {
  ActiveTheme,
  Banner,
  BannerInput,
  BannerPlacement,
  ContrastReport,
  PublicBanner,
  ThemePreset,
  ThemeTokens,
  ThemeVersion,
} from '@/entities/site/types'

/** Действующее оформление. Читается при запуске — и до входа тоже (ТЗ §22). */
export function useActiveTheme() {
  return useQuery({
    queryKey: ['site', 'theme'],
    queryFn: () => api.get<ActiveTheme | null>('/site/theme'),
    // Тема меняется редко, но после отката должна подхватиться без
    // перезагрузки вкладки.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}

export function useBanners(placement: BannerPlacement) {
  return useQuery({
    queryKey: ['site', 'banners', placement],
    queryFn: () => api.get<PublicBanner[]>(`/site/banners?placement=${placement}`),
    staleTime: 60 * 1000,
  })
}

export function useBannerClicked() {
  return useMutation({
    mutationFn: (bannerId: string) =>
      api.post<{ ok: boolean }>(`/site/banners/${bannerId}/clicked`),
  })
}

export function useBannerDismissed() {
  return useMutation({
    mutationFn: (bannerId: string) =>
      api.post<{ ok: boolean }>(`/site/banners/${bannerId}/dismissed`),
  })
}

// --- Управление: только администратор ---

export function useThemePresets() {
  return useQuery({
    queryKey: ['admin', 'site', 'themes'],
    queryFn: () => api.get<ThemePreset[]>('/admin/site/themes'),
  })
}

export function useThemeHistory() {
  return useQuery({
    queryKey: ['admin', 'site', 'themes', 'history'],
    queryFn: () => api.get<ThemeVersion[]>('/admin/site/themes/history'),
  })
}

export function useCheckContrast() {
  return useMutation({
    mutationFn: (tokens: ThemeTokens) =>
      api.post<ContrastReport>('/admin/site/themes/check', { tokens }),
  })
}

export function useAddThemeVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      presetId,
      tokens,
      notes,
    }: {
      presetId: string
      tokens: ThemeTokens
      notes?: string
    }) =>
      api.post<ThemeVersion>(`/admin/site/themes/${presetId}/versions`, { tokens, notes }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'site', 'themes'] }),
  })
}

/** Включает версию оформления. Тем же действием делается откат (ТЗ §22). */
export function useActivateTheme() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ versionId, reason }: { versionId: string; reason?: string }) =>
      api.post<ActiveTheme>('/admin/site/themes/activate', {
        version_id: versionId,
        reason,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['site', 'theme'], data)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'site', 'themes'] })
    },
  })
}

export function useAdminBanners() {
  return useQuery({
    queryKey: ['admin', 'site', 'banners'],
    queryFn: () => api.get<Banner[]>('/admin/site/banners'),
  })
}

function invalidateBanners(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['admin', 'site', 'banners'] })
  void queryClient.invalidateQueries({ queryKey: ['site', 'banners'] })
}

export function useCreateBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BannerInput) => api.post<Banner>('/admin/site/banners', input),
    onSuccess: () => invalidateBanners(queryClient),
  })
}

export function useUpdateBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<BannerInput> & { id: string }) =>
      api.patch<Banner>(`/admin/site/banners/${id}`, input),
    onSuccess: () => invalidateBanners(queryClient),
  })
}

export function usePublishBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<Banner>(`/admin/site/banners/${id}/publish`),
    onSuccess: () => invalidateBanners(queryClient),
  })
}

export function useArchiveBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<Banner>(`/admin/site/banners/${id}/archive`),
    onSuccess: () => invalidateBanners(queryClient),
  })
}
